import React, { useRef, useEffect, useState, memo, useCallback, useMemo } from 'react';
import axios from 'axios';
import Hls from 'hls.js';
import styles from './ShowItem.module.css';
import playIcon from '../icons/play.png';
import pauseIcon from '../icons/pause.png';
import profileOfflineIcon from '../icons/profileoffline.png';

const ShowItem = ({
  id,
  coverImage,
  title,
  genre,
  artist,
  specialGuests = [],
  supportActs = [],
  city,
  venue,
  date,
  timeStart,
  artistSticker,

  sendEvent,
  isPlaying,
  onPlay,
  onTagClick,
  selectedTags,
  highlighted,
  isAuthenticated,
  onOpenAuthModal,

  // залишаю пропси для сумісності, але пріоритет за бекенд-конфігом
  priceTier1,
  priceTier2,
}) => {
  const audioRef = useRef(null);
  const hlsRef = useRef(null);
  const currentConcertIdRef = useRef(null);

  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const itemRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isPreparing, setIsPreparing] = useState(false);

  // конфіг з бекенда: значення зберігаємо, але рендер кнопок керується окремими ready-прапорцями
  const [ticketCfg, setTicketCfg] = useState({
    showL1: true,
    showL2: true,
    priceL1: typeof priceTier1 === 'number' ? priceTier1 : 600,
    priceL2: typeof priceTier2 === 'number' ? priceTier2 : 400,
  });

  // Головне — пер-кнопкові прапорці готовності, щоб уникнути миготіння
  const [readyL1, setReadyL1] = useState(false);
  const [readyL2, setReadyL2] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get('/api/payments/ticket-config', { timeout: 7000 });
        if (!alive) return;
        const showL1 = !!data?.showL1;
        const showL2 = !!data?.showL2;
        const priceL1 = Number.isFinite(data?.priceL1Uah) ? Math.round(data.priceL1Uah) : (typeof priceTier1 === 'number' ? Math.round(priceTier1) : NaN);
        const priceL2 = Number.isFinite(data?.priceL2Uah) ? Math.round(data.priceL2Uah) : (typeof priceTier2 === 'number' ? Math.round(priceTier2) : NaN);

        setTicketCfg({ showL1, showL2, priceL1, priceL2 });

        // показуємо конкретні кнопки лише коли точно знаємо, що їх треба показувати
        setReadyL1(showL1 && Number.isFinite(priceL1));
        setReadyL2(showL2 && Number.isFinite(priceL2));
      } catch {
        // без зміни стилів: просто логічно виводимо на основі пропсів (якщо є)
        setReadyL1(Number.isFinite(priceTier1));
        setReadyL2(Number.isFinite(priceTier2));
        setTicketCfg((old) => ({
          ...old,
          priceL1: Number.isFinite(priceTier1) ? Math.round(priceTier1) : old.priceL1,
          priceL2: Number.isFinite(priceTier2) ? Math.round(priceTier2) : old.priceL2,
        }));
      }
    })();
    return () => { alive = false; };
  }, [priceTier1, priceTier2]);

  const absoluteCover = useMemo(
    () => new URL(coverImage, window.location.origin).href,
    [coverImage]
  );

  const setMediaMetadata = useCallback(() => {
    if (!('mediaSession' in navigator)) return;
    const guestStr   = specialGuests?.length ? ` feat. ${specialGuests.join(', ')}` : '';
    const supportStr = supportActs?.length   ? ` • support: ${supportActs.join(', ')}` : '';
    const artistLine = `${artist || 'Unknown'}${guestStr}${supportStr}`;
    navigator.mediaSession.metadata = new MediaMetadata({
      title,
      artist: artistLine,
      album: title,
      artwork: [
        { src: absoluteCover, sizes: '512x512', type: 'image/png' },
        { src: absoluteCover, sizes: '256x256', type: 'image/png' }
      ]
    });
  }, [title, artist, specialGuests, supportActs, absoluteCover]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => {
        setMessage(null);
        setMessageType(null);
      }, 2100);
      return () => clearTimeout(t);
    }
  }, [message]);

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/share?id=${id}`;
    if (navigator.share) {
      navigator
        .share({ title: `Подивись цю подію: ${title}`, url: shareUrl })
        .catch(() =>
          setMessage({ text: 'Упс, не зміг поділитись, спробуй скопіювати!', type: 'error' })
        );
    } else {
      navigator.clipboard.writeText(shareUrl);
      setMessage({ text: 'Обрану подію скопійовано до буферу!', type: 'success' });
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      axios
        .get('/api/favorites/list')
        .then((response) => {
          const liked = response.data.some((concert) => concert.id === id);
          setIsLiked(liked);
        })
        .catch(() => {});
    } else {
      setIsLiked(false);
    }
  }, [id, isAuthenticated]);

  const toggleLike = () => {
    if (!isAuthenticated) {
      setMessage({
        text: (
          <span>
            Тисни на <img src={profileOfflineIcon} alt="Icon" className={styles.inlineIcon} /> перед тим, як зберегти це!
          </span>
        ),
        type: 'error',
      });
      return;
    }

    if (isLiked) {
      axios
        .post('/api/favorites/remove', { concertId: id })
        .then(() => {
          setIsLiked(false);
          window.dispatchEvent(new CustomEvent('likedBeatsChanged'));
          setMessage({ text: 'Подію видалено з улюблених.', type: 'success' });
        })
        .catch(() => setMessage({ text: 'Помилка при видаленні, спробуй пізніше!', type: 'error' }));
    } else {
      axios
        .post('/api/favorites/add', { concertId: id })
        .then(() => {
          setIsLiked(true);
          window.dispatchEvent(new CustomEvent('likedBeatsChanged'));
          setMessage({ text: 'Подію додано до улюблених!', type: 'success' });
        })
        .catch(() => setMessage({ text: 'Помилка додавання, спробуй пізніше!', type: 'error' }));
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    const handleEnded = () => {
      onPlay(null);
      setIsAudioPlaying(false);
    };
    audio.addEventListener('ended', handleEnded);
    return () => { audio.removeEventListener('ended', handleEnded); };
  }, [onPlay]);

  useEffect(() => {
    const audio = audioRef.current;
    const updateTime = () => {
      setCurrentTime(audio.currentTime || 0);
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    };
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateTime);
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateTime);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    const syncState = () => {
      const isCurrentlyPlaying = !audio.paused && !audio.ended;
      if (isCurrentlyPlaying !== isPlaying) {
        onPlay(isCurrentlyPlaying ? id : null);
      }
    };
    audio.addEventListener('play', syncState);
    audio.addEventListener('pause', syncState);
    audio.addEventListener('ended', syncState);
    return () => {
      audio.removeEventListener('play', syncState);
      audio.removeEventListener('pause', syncState);
      audio.removeEventListener('ended', syncState);
    };
  }, [isPlaying, onPlay, id]);

  const getPlaylistUrl = useCallback(async (concertId) => {
    const r = await fetch(`/api/hls/url/by-concert/${concertId}`);
    if (!r.ok) throw new Error('Failed to get playlist URL');
    const { playlist } = await r.json();
    return playlist;
  }, []);

  const getBufferedAhead = (media) => {
    if (!media) return 0;
    const { buffered, currentTime } = media;
    for (let i = 0; i < buffered.length; i++) {
      const start = buffered.start(i);
      const end = buffered.end(i);
      if (currentTime >= start && currentTime <= end) {
        return Math.max(0, end - currentTime);
      }
    }
    return 0;
  };

  const bufferMonitorRef = useRef(null);
  const seekHandlerRef = useRef(null);
  const lastStartRef = useRef(0);
  const lastStopRef = useRef(0);
  const isSeekingRef = useRef(false);
  const seekedHandlerRef = useRef(null);

  const destroyHls = useCallback(() => {
    const a = audioRef.current;
    if (bufferMonitorRef.current && a) {
      a.removeEventListener('timeupdate', bufferMonitorRef.current);
      bufferMonitorRef.current = null;
    }
    if (seekHandlerRef.current && a) {
      a.removeEventListener('seeking', seekHandlerRef.current);
      seekHandlerRef.current = null;
    }
    if (seekedHandlerRef.current && a) {
      a.removeEventListener('seeked', seekedHandlerRef.current);
      seekedHandlerRef.current = null;
    }
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    lastStartRef.current = 0;
    lastStopRef.current = 0;
    isSeekingRef.current = false;
  }, []);

  const updatePositionState = useCallback(() => {
    if (!('mediaSession' in navigator) || !navigator.mediaSession.setPositionState) return;
    const a = audioRef.current;
    if (!a) return;
    const dur = isFinite(a.duration) ? a.duration : 0;
    navigator.mediaSession.setPositionState({
      duration: dur,
      position: a.currentTime || 0,
      playbackRate: a.playbackRate || 1
    });
  }, []);

  const setupHlsAndPlay = useCallback(async (concertId) => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    destroyHls();

    const playlist = await getPlaylistUrl(concertId);
    currentConcertIdRef.current = concertId;

    if (Hls.isSupported()) {
      const hls = new Hls({
        autoStartLoad: true,
        maxBufferLength: 3,
        maxMaxBufferLength: 6,
        backBufferLength: 0,
        lowLatencyMode: false
      });
      hlsRef.current = hls;
      hls.loadSource(playlist);
      hls.attachMedia(audioEl);

      let stopMargin = 6;
      let resumeMargin = 2;

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        const td = hls.levels?.[hls.currentLevel]?.details?.targetduration;
        if (td) {
          stopMargin = Math.max(3, td * 2.5);
          resumeMargin = Math.max(1, td * 1.0);
        }
        audioEl.play().then(updatePositionState).catch(() => {});
      });

      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        if (isSeekingRef.current) return;
        const ahead = getBufferedAhead(audioEl);
        const now = performance.now();
        if (ahead > stopMargin && now - lastStopRef.current > 500) {
          hls.stopLoad();
          lastStopRef.current = now;
        }
      });

      if (bufferMonitorRef.current) {
        audioEl.removeEventListener('timeupdate', bufferMonitorRef.current);
        bufferMonitorRef.current = null;
      }
      bufferMonitorRef.current = () => {
        if (isSeekingRef.current) return;
        const ahead = getBufferedAhead(audioEl);
        const now = performance.now();
        if (ahead < resumeMargin && now - lastStartRef.current > 500) {
          hlsRef.current?.startLoad();
          lastStartRef.current = now;
        }
      };
      audioEl.addEventListener('timeupdate', bufferMonitorRef.current);

      if (seekHandlerRef.current) {
        audioEl.removeEventListener('seeking', seekHandlerRef.current);
        seekHandlerRef.current = null;
      }
      seekHandlerRef.current = () => {
        isSeekingRef.current = true;
        const pos = (audioEl.currentTime || 0) + 0.05;
        lastStartRef.current = performance.now();
        hlsRef.current?.startLoad(pos);
      };
      audioEl.addEventListener('seeking', seekHandlerRef.current);

      if (seekedHandlerRef.current) {
        audioEl.removeEventListener('seeked', seekedHandlerRef.current);
        seekedHandlerRef.current = null;
      }
      seekedHandlerRef.current = () => {
        isSeekingRef.current = false;
        lastStartRef.current = performance.now();
      };
      audioEl.addEventListener('seeked', seekedHandlerRef.current);

      hls.on(Hls.Events.ERROR, async (_evt, data) => {
        if (data.fatal && data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          try {
            const fresh = await getPlaylistUrl(currentConcertIdRef.current);
            hls.loadSource(fresh);
            hls.startLoad();
          } catch (e) {
            console.error('HLS refresh failed', e);
          }
        }
      });
    } else if (audioEl.canPlayType('application/vnd.apple.mpegurl')) {
      audioEl.src = playlist;
      await audioEl.play().then(updatePositionState).catch(() => {});
    } else {
      console.error('HLS is not supported in this browser.');
    }
  }, [destroyHls, getPlaylistUrl, updatePositionState]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
  }, [isPlaying]);

  useEffect(() => {
    if (!('mediaSession' in navigator) || !navigator.mediaSession.setPositionState) return;
    const a = audioRef.current;
    if (!a) return;

    const handler = () => updatePositionState();

    a.addEventListener('timeupdate', handler);
    a.addEventListener('durationchange', handler);
    a.addEventListener('seeked', handler);
    a.addEventListener('ratechange', handler);

    updatePositionState();

    return () => {
      a.removeEventListener('timeupdate', handler);
      a.removeEventListener('durationchange', handler);
      a.removeEventListener('seeked', handler);
      a.removeEventListener('ratechange', handler);
    };
  }, [updatePositionState]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const start = async () => {
      if (!audio.paused) return;

      if (!audio.src && !hlsRef.current) {
        try {
          await setupHlsAndPlay(id);
          setMediaMetadata();
          setIsAudioPlaying(true);
          if ('mediaSession' in navigator) {
            navigator.mediaSession.setActionHandler('play',  () => onPlay(id));
            navigator.mediaSession.setActionHandler('pause', () => onPlay(null));
          }
          updatePositionState();
        } catch (err) {
          console.error(err);
          setIsAudioPlaying(false);
        }
      } else {
        audio.play()
          .then(() => {
            setMediaMetadata();
            setIsAudioPlaying(true);
            updatePositionState();
          })
          .catch(() => {});
      }
    };

    const stop = () => {
      if (audio.paused) return;
      audio.pause();
      setIsAudioPlaying(false);
      if ('mediaSession' in navigator) {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
      }
    };

    if (isPlaying) start(); else stop();
  }, [isPlaying, isAudioPlaying, onPlay, id, setupHlsAndPlay, updatePositionState, setMediaMetadata]);

  useEffect(() => { if (isPlaying) setMediaMetadata(); }, [isPlaying, setMediaMetadata]);
  useEffect(() => destroyHls, [destroyHls]);

  const handleBuy = async (amountUah, concertId, productType) => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      onPlay(null);
    }

	    if (!isAuthenticated) {
	  onOpenAuthModal?.();
	  return;
	}

	const userId = localStorage.getItem('userId');
	if (!userId) {
	  onOpenAuthModal?.();
	  return;
	}

    setIsPreparing(true);

    try {
      try {
	  await sendEvent(
	  `Purchase ${productType.toUpperCase()} for "${title}"`,
	  {
	    concertId,
	    productType,
	    amount: Math.round(amountUah),
	    title,
	  }
	);
      } catch {}

      const { data } = await axios.post('/api/payments/create-invoice', {
        userId,
        amount: Math.round(amountUah), // у гривнях, ціле
        concertId,
        productType, // 'pre' | 'full'
      });

      if (data?.invoiceUrl) {
        window.location.href = data.invoiceUrl;
      } else {
        setMessage({ text: 'Щось не зрослося з рахунком. Спробуй пізніше!', type: 'error' });
      }
    } catch {
      setMessage({ text: 'Щось не зрослося з рахунком. Спробуй пізніше!', type: 'error' });
    } finally {
      setIsPreparing(false);
    }
  };

  const handleDesktopWaveformClick = (e) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const ratio = clickX / rect.width;
    audioRef.current.currentTime = ratio * duration;
  };

  const handleMobileCoverClick = (e) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const x = e.clientX - centerX;
    const y = e.clientY - centerY;
    const angle = Math.atan2(x, -y);
    let deg = angle * (180 / Math.PI);
    if (deg < 0) deg += 360;
    const ratio = deg / 360;
    audioRef.current.currentTime = ratio * duration;
  };

  const loadAndPlayAudio = async () => {
    if (!hlsRef.current && !audioRef.current.src) {
      await setupHlsAndPlay(id);
    }
    onPlay(id);
  };

  const isCancelledShow = useMemo(() => {
  return String(date || '').trim() === '15.03.2026';
}, [date]);

  const isKeepOpenShow = useMemo(() => {
  return String(date || '').trim() === '31.10.2024';
}, [date]);

const isTicketsClosed = useMemo(() => {
  if (isCancelledShow || isKeepOpenShow) return false;

  // date: "DD.MM.YYYY"
  const parts = String(date || '').split('.');
  if (parts.length !== 3) return false;

  const d = Number(parts[0]);
  const m = Number(parts[1]);
  const y = Number(parts[2]);
  if (!d || !m || !y) return false;

  const [hh, mm] = String(timeStart || '00:00').split(':').map(Number);

  // локальний час (Україна)
  const showDate = new Date(
    y,
    m - 1,
    d,
    Number.isFinite(hh) ? hh : 0,
    Number.isFinite(mm) ? mm : 0,
    0,
    0
  );

  return showDate.getTime() < Date.now();
}, [date, timeStart, isCancelledShow, isKeepOpenShow]);

  return (
    <div
      id={`show-${id}`}
      ref={itemRef}
      className={`${styles.showItem} ${highlighted ? styles.highlighted : ''}`}
    >
      {message && (
        <div className={styles.overlay}>
          <div className={`${styles.overlayMessage} ${styles[messageType]}`}>
            {message.text}
          </div>
        </div>
      )}

      <button onClick={loadAndPlayAudio} className={styles.playButton}>
        <img src={isPlaying ? pauseIcon : playIcon} alt="Play/Pause" className={styles.playButtonImage}/>
      </button>

      {isMobile ? (
        <div
          className={styles.coverContainer}
          style={{ '--progress': `${duration ? (currentTime / duration) * 360 : 0}deg` }}
          onClick={handleMobileCoverClick}
        >
          <img src={coverImage} alt={`${title} cover`} className={styles.showItemCoverMobile} loading="lazy" />
        </div>
      ) : (
        <div className={styles.showCover}>
          <img src={coverImage} alt={`${title} cover`} className={styles.showItemCover} loading="lazy" />
          {artistSticker && (
            <img
              src={artistSticker}
              alt="Artist Sticker"
              className={`${styles.artistSticker} ${styles.artistStickerDesktop}`}
              onClick={() => onTagClick(artistSticker)}
            />
          )}
        </div>
      )}

      <div className={styles.showInfo}>
        <h3 className={styles.showTitle}>{title}</h3>
        <p className={styles.showGenre}>{genre}</p>

        <div className={styles.showMeta}>
          <span className={styles.showLine}><strong>Хедлайнер:</strong> {artist}</span>
          {(specialGuests.length > 0 || supportActs.length > 0) && (
            <span className={styles.showLine}>
              {specialGuests.length > 0 && <span><strong>Спец-гості:</strong> {specialGuests.join(', ')}</span>}
              {specialGuests.length > 0 && supportActs.length > 0 && <span> · </span>}
              {supportActs.length > 0 && <span><strong>Розігрів:</strong> {supportActs.join(', ')}</span>}
            </span>
          )}
          <span className={styles.showLine}><strong>Дата та час:</strong> {date} · {timeStart}</span>
          <span className={styles.showLine}><strong>Локація:</strong> {city}, {venue}</span>
        </div>

        <div className={styles.showTags}>
          {genre && (
            <button
              className={`${styles.showTag} ${selectedTags.includes(genre) ? styles.activeShowTag : ''}`}
              onClick={() => onTagClick(genre)}
            >
              {genre}
            </button>
          )}
          {city && (
            <button
              className={`${styles.showTag} ${selectedTags.includes(city) ? styles.activeShowTag : ''}`}
              onClick={() => onTagClick(city)}
            >
              {city}
            </button>
          )}
        </div>
      </div>

      {!isMobile && (
        <div className={styles.waveformContainer} onClick={handleDesktopWaveformClick}>
          <div
            className={styles.waveformProgress}
            style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
          />
        </div>
      )}

      <div className={styles.showActions}>
        <button className={styles.shareButton} onClick={handleShare}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="#555555" viewBox="0 0 24 24" width="24" height="24">
            <path d="M18 16.08c-.76 0-1.44.3-1.96.77l-7.1-4.2a3.11 3.11 0 0 0 0-1.29l7.1-4.2A2.996 2.996 0 1 0 15 5c0 .11.01.21.02.32l-7.1 4.2a3 3 0 1 0 0 4.96l7.1 4.2c-.01.11-.02.21-.02.32a3 3 0 1 0 3-3zM18 4a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm-12 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm12 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
          </svg>
        </button>

        <button className={styles.showFavorite} onClick={toggleLike}>
          {isLiked ? (
            <svg xmlns="http://www.w3.org/2000/svg" fill="#ffffff" viewBox="0 0 24 24" width="24" height="24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" fill="#555555" viewBox="0 0 24 24" width="24" height="24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          )}
        </button>

        {isMobile && artistSticker && (
          <img
            src={artistSticker}
            alt="Artist Sticker"
            className={`${styles.artistSticker} ${styles.artistStickerMobile}`}
            onClick={() => onTagClick(artistSticker)}
          />
        )}

  <div
  className={`${styles.showPrice} ${(isTicketsClosed || isCancelledShow) ? styles.showPriceLocked : ''}`}
>
  {(isTicketsClosed || isCancelledShow) && (
    <div className={styles.priceLockOverlay}>
      <div className={styles.priceLockText}>
        {isCancelledShow ? 'Скасовано' : 'Розпродано'}
      </div>
    </div>
  )}

  {readyL1 && (
    <button
      className={`${styles.showBuyTier1} ${(isTicketsClosed || isCancelledShow) ? styles.buyDisabled : ''}`}
      disabled={isTicketsClosed || isCancelledShow}
      onClick={(isTicketsClosed || isCancelledShow) ? undefined : () => handleBuy(Math.round(ticketCfg.priceL1), id, 'pre')}
    >
      КВИТОК(L1) — ₴{Math.round(ticketCfg.priceL1)}
    </button>
  )}

  {readyL2 && (
    <button
      className={`${styles.showBuyTier2} ${(isTicketsClosed || isCancelledShow) ? styles.buyDisabled : ''}`}
      disabled={isTicketsClosed || isCancelledShow}
      onClick={(isTicketsClosed || isCancelledShow) ? undefined : () => handleBuy(Math.round(ticketCfg.priceL2), id, 'full')}
    >
      КВИТОК(L2) — ₴{Math.round(ticketCfg.priceL2)}
    </button>
  )}
</div>

</div>
      <audio ref={audioRef} preload="metadata" />

      {isPreparing && (
        <div className={styles.buyOverlay}>
          <div className={styles.buyPreparing}>ПІДГОТОВКА…</div>
        </div>
      )}
    </div>
  );
};

export default memo(ShowItem);

